import * as d3 from "d3";
import { AXIS_LABEL_SIZE, AXIS_POINT_SIZE } from "../constants";
import Utils from "../Utility/Utils";

class ScoreGraph {

    constructor(chartArea, data) {
        this.MARGIN = { LEFT: 40, RIGHT: 0, TOP: 50, BOTTOM: 130 };
        this.WIDTH = chartArea.clientWidth;
        this.HEIGHT = chartArea.clientHeight;
        this.totalPoint = document.querySelector('#country-scorecard .score_block .total_points .point')
        this.decimalPoint = document.querySelector('#country-scorecard .score_block .total_points .unit-block .decimal')

        this.topCells = document.querySelectorAll('#country-scorecard .legend-row .axis-grid .cell')
        this.titleBlock = document.querySelector('#country-scorecard .titleBlock .main_title')
        this.countryFlag = document.querySelector('#country-scorecard .titleBlock img')
        this.countryImage = document.querySelector('#country-scorecard .bottom-row .img-block')

        this.data = data

        this.svg = d3
            .select("#chart-area")
            .append("svg")
            .attr("width", this.WIDTH)
            .attr("height", this.HEIGHT);

        this.groupBorderColor = {
            Human: "#7054CC",
            Physical: "#007D59",
            Innovation: "#21405C",
            Institution: "#00B0FF",
            Natural: "#B07D24",
            Social: "#026CCA",
        }

        this.shortCountryName = Utils.SHORT_COUNTRY_NAMES

        this.groupColors = {
            Human: "#9487E5",
            Physical: "#00A699",
            Innovation: "#427DA1",
            Institution: "#4AD1FF",
            Natural: "#EDBA00",
            Social: "#359FFD",
        };

        this.groupBackgroundColors = {
            Human: "#CCC9ED",
            Physical: "#4ADEC7",
            Innovation: "#CCDEEB",
            Institution: "#4DFFFA",
            Natural: "#FFDB7A",
            Social: "#B3DBFE",
        };

        // Define the groups and their corresponding ranges in `groupedData`
        this.groupRanges = [
            { group: "Human", range: [0, 5] },
            { group: "Physical", range: [7, 11] },
            { group: "Innovation", range: [13, 15] },
            { group: "Institution", range: [17, 18] },
            { group: "Natural", range: [20, 22] },
            { group: "Social", range: [24, 24] },
        ];

        if (!this.data || !Array.isArray(this.data)) {
            console.error('Invalid data format for dropdown population:', this.data);
            return;
        } else {
            this.updateGraph("Argentina")
            this.adjustXAxisGrid(chartArea)
        }
    }

    clearGraph() {
        // Clear all elements inside the SVG
        this.svg.selectAll("*").remove();
    }

    updateGraph(country) {
        this.clearGraph()
        const filteredData = this.data.filter(d => d.Country == country);
        const actualData = filteredData[0];

        this.countryFlag.src = `assets/images/Flags/${actualData.Flag}`
        this.titleBlock.textContent = this.shortCountryName[actualData.Country] || actualData.Country

        const regionEncoded = encodeURIComponent(actualData.Region);
        const countryEncoded = encodeURIComponent(actualData.Country);

        this.countryImage.style.backgroundImage = `url(assets/images/country_images/${regionEncoded}/${countryEncoded}.webp)`

        const bgGroup = this.svg.append("g").attr("class", "bg-group");

        let numericalDataArray = [];
        let otherDataArray = [];

        for (let key in actualData) {
            if (actualData.hasOwnProperty(key)) {
                if (key === "Total") {
                    continue;
                }

                if (typeof actualData[key] === 'number') {
                    // Push numerical key-value pairs as arrays
                    numericalDataArray.push([key, actualData[key]]);
                } else {
                    // Push non-numerical key-value pairs as arrays
                    otherDataArray.push([key, actualData[key]]);
                }
            }
        }

        let total = numericalDataArray.reduce((sum, [key, value]) => sum + value, 0);

        const integerPart = parseInt(Utils.roundUpToDecimal(total, 1))
        const decimalPart = parseInt(Utils.extractDecimalDigit(total, 1))

        this.totalPoint.textContent = integerPart
        if (decimalPart != undefined) {
            this.decimalPoint.textContent = "." + decimalPart
        } else {
            this.decimalPoint.textContent = ""
        }

        const groupedData = [
            null,
            { label: "Life Expectancy", value: actualData.Life_expectancy, group: "Human" },
            { label: "Suicide", value: actualData.Suicide_mortality, group: "Human" },
            { label: "Tertiary Education", value: actualData.Percent_of_population, group: "Human" },
            { label: "Age Dependency", value: actualData.Age_dependency_ratio, group: "Human" },
            { label: "Human Capital", value: actualData.Human_capital, group: "Human" },
            null, // Gap
            { label: "Physical Capital", value: actualData.Physical_capital, group: "Physical" },
            { label: "Internet Usage", value: actualData.Individuals_using_internet, group: "Physical" },
            { label: "Logistics", value: actualData.World_Bank_logistics, group: "Physical" },
            { label: "Secure Internet", value: actualData.Secure_internet, group: "Physical" },
            { label: "Electricity Access", value: actualData.Access_to_electricity, group: "Physical" },
            null, // Gap
            { label: "Patents", value: actualData.Patent_applications, group: "Innovation" },
            { label: "Science Journals", value: actualData.Science_journal, group: "Innovation" },
            { label: "Sector Concentration", value: actualData.Hirschman_Index, group: "Innovation" },
            null, // Gap
            { label: "Institutional Quality", value: actualData.World_Bank_institutional, group: "Institution" },
            { label: "Inequality", value: actualData.Inequality_principal, group: "Institution" },
            null, // Gap
            { label: "Natural Capital", value: actualData.Natural_capital, group: "Natural" },
            { label: "Pollution", value: actualData.PM2_5, group: "Natural" },
            { label: "Water Stress", value: actualData.Water_stress, group: "Natural" },
            null, // Gap
            { label: "Social Trust", value: actualData.Trust, group: "Social" },
            null
        ];

        const filteredData2 = groupedData.filter(d => d !== null);

        const xScale = d3
            .scaleBand()
            .domain(groupedData.map((d, i) => i)) // Include gaps
            .range([this.MARGIN.LEFT, this.WIDTH - this.MARGIN.RIGHT])
            .paddingInner(0.6)
            .paddingOuter(0.2);

        const minValue = d3.min(filteredData2, d => d.value)

        const yScale = d3
            .scaleLinear()
            .domain([
                minValue - 1, // Minimum value
                d3.max(filteredData2, d => d.value)  // Maximum value
            ])
            .range([this.HEIGHT, this.MARGIN.TOP]);





        this.svg.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${this.MARGIN.LEFT}, 0)`)
            .call(d3.axisLeft(yScale).ticks(5).tickSize(0).tickPadding(0))
            .selectAll("path, line, text")
            .style('font-size', AXIS_LABEL_SIZE)
            .style("stroke", "none");

        this.svg.append("g")
            .selectAll("line")
            .data(yScale.ticks(5))
            .join("line")
            .attr('class', 'y-axis-line')
            .attr("x1", this.MARGIN.LEFT)
            .attr("x2", this.WIDTH)
            .attr("y1", d => yScale(d))
            .attr("y2", d => yScale(d))
            .attr("stroke", "rgba(225, 227, 228, 1)")
            .attr("stroke-dasharray", "2,2")
            .attr("stroke-width", 1);


        const xAxisGroup = this.svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${this.HEIGHT})`) // Move x-axis to the bottom
            .call(
                d3.axisBottom(xScale)
                    .tickValues(groupedData.map((d, i) => (d ? i : null)).filter(d => d !== null)) // Exclude null gaps
                    .tickFormat((d, i) => groupedData[d]?.label || "") // Map index to label
                    .tickSize(5)
            );


        xAxisGroup.select(".domain")
            .attr("stroke", "transparent");

        xAxisGroup.selectAll("text")
            .attr("text-anchor", "end")
            .attr('font-family', "Arial")
            .attr('font-size', AXIS_LABEL_SIZE + 'px')
            .each(function (d) {
                const label = groupedData[d]?.label || "";
                const parts = label.split(" ");

                if (parts.length >= 2) {
                    // If parts are more than 2, split into multiple lines and adjust transform
                    d3.select(this)
                        .attr("transform", "rotate(-90) translate(-20,-10)") // Adjust transform for multi-line labels
                        .text(null) // Clear the original label
                        .selectAll("tspan")
                        .data(parts)
                        .join("tspan")
                        .attr("x", 0) // Align vertically
                        .attr("dy", (d, i) => i * 15) // Offset each line by 15px
                        .text(d => d); // Set the text for each part
                } else {
                    // If parts are less than 2, keep the label as a single line and adjust transform
                    d3.select(this)
                        .attr("transform", "rotate(-90) translate(-20,-10)") // Adjust transform for single-line labels
                        .text(label);
                }
            })



        const baseline = yScale(0);

        this.xLine = this.svg
            .append("line")
            .attr("x1", this.MARGIN.LEFT + 15)
            .attr("x2", this.WIDTH - 20)
            .attr("y1", baseline)
            .attr("y2", baseline)
            .attr("stroke", "#000")
            .attr("stroke-width", 1);



        this.baseline = this.svg
            .append("line")
            .attr("x1", this.MARGIN.LEFT)
            .attr("x2", this.WIDTH)
            .attr("y1", this.HEIGHT)
            .attr("y2", this.HEIGHT)
            .attr("stroke", "rgba(0, 0, 0, 1)")
            .attr("stroke-dasharray", "2,2")
            .attr("stroke-width", 1);

        const whiteGradient = this.svg.append("defs")
            .append("linearGradient")
            .attr("id", "whiteGradient")
            .attr("x1", "0%")  // Start at the left side
            .attr("y1", "100%") // Start from the bottom (100% of the height)
            .attr("x2", "0%")  // End at the left side
            .attr("y2", "0%")  // End at the top (0% of the height)
            .selectAll("stop")
            .data([
                { offset: "0%", color: "white", opacity: 0 },
                { offset: "50%", color: "white", opacity: 0.5 },
                { offset: "100%", color: "white", opacity: 0 }
            ])
            .enter().append("stop")
            .attr("offset", d => d.offset)
            .attr("stop-color", d => d.color)
            .attr("stop-opacity", d => d.opacity);



        this.groupRanges.forEach(({ group, range }, index) => {
            const nullWidth = xScale.bandwidth() / 2; // Half the width of a null space

            bgGroup
                .append("rect")
                .attr(
                    "x",
                    index === 0
                        ? xScale(range[0]) + 10 // No adjustment for the first rectangle
                        : xScale(range[0]) - nullWidth * 4 // Adjust for other rectangles
                )
                .attr("y", 0) // Top of the chart
                .attr(
                    "width",
                    index === 0
                        ? xScale(range[1] + 1) - xScale(range[0]) + nullWidth - 10 // No left null adjustment for the first rectangle
                        : xScale(range[1] + 1) - xScale(range[0]) + nullWidth * 5 // Include null space on both sides
                )
                .attr("height", this.HEIGHT) // Height spans the chart
                .attr("fill", this.groupColors[group]) // Background color for the group
                .attr("opacity", 0.1); // Adjust opacity for visibility
        });


        this.svg
            .append("g")
            .attr("class", "line-group")
            .selectAll("line")
            .data(groupedData)
            .join("line")
            .attr("x1", (d, i) => xScale(i) + xScale.bandwidth() / 2) // Center of the bar
            .attr("x2", (d, i) => xScale(i) + xScale.bandwidth() / 2) // Same as x1
            .attr("y1", 0) // Start at the top of the chart
            .attr("y2", this.HEIGHT) // End at the bottom of the chart
            .attr("stroke", "rgba(0, 0, 0, 0.1)") // Line color
            .attr("stroke-width", d => (d ? 1 : 0)) // Line width
            .attr("stroke-dasharray", "4 4"); // Dashed line pattern


        this.svg
            .append("g")
            .selectAll("rect")
            .data(groupedData)
            .join("rect")
            .attr("x", (d, i) => xScale(i))
            .attr("data-label", d => d != null ? d.label : "")
            .attr("y", baseline) // Start at the baseline
            .attr("height", 0) // Initial height is 0 for the animation
            .attr("width", xScale.bandwidth())
            .attr("fill", d => (d ? this.groupBackgroundColors[d.group] : "none")) // Skip gaps
            .attr("opacity", 1)
            .transition() // Apply the animation
            .duration(1000) // Animation duration in milliseconds
            .ease(d3.easePoly.exponent(1)) // Optional easing function
            .attr("y", d => (d?.value >= 0 ? yScale(d.value) : baseline)) // Final y position
            .attr("height", d => (d ? Math.abs(yScale(d.value) - baseline) : 0)); // Final height



        this.basegradientBar = this.svg
            .append('rect')
            .attr('class', 'gradient-rect')
            .attr('width', this.WIDTH - this.MARGIN.LEFT - 35)
            .attr('x', this.MARGIN.LEFT + 18)
            .attr('y', baseline - 15)
            .attr('height', 30)
            .attr('fill', "url(#whiteGradient)")

        this.svg
            .append("g")
            .selectAll("circle")
            .data(groupedData)
            .join("circle")
            .attr("cx", (d, i) => xScale(i) + xScale.bandwidth() / 2)
            .attr("cy", baseline) // Start at the baseline
            .attr("r", 8) // Initial radius is 0 for the animation
            .attr("fill", d => (d ? this.groupColors[d.group] : "none")) // Skip gaps
            .attr("stroke", d => (d ? this.groupBorderColor[d.group] : "none")) // Skip gaps
            .attr("stroke-width", 2)
            .transition() // Apply the animation
            .duration(1000) // Animation duration in milliseconds
            .ease(d3.easePoly.exponent(1)) // Optional easing function
            .attr("cy", d => (d ? yScale(d.value) : baseline)) // Final y position

        // this.svg.append("g")
        //     .selectAll("text")
        //     .data(groupedData)
        //     .join("text")
        //     .attr('x', (d, i) => xScale(i) + xScale.bandwidth() / 2)
        //     .attr('y', d => (d ? yScale(d.value) : baseline))
        //     .text(d => d ? d.label : '')

        // PUPOLATE X AXIS LABELS
        this.populateXAxisLabels(groupedData)
        this.updateTopLegendValues(groupedData)
    }

    updateTopLegendValues(data) {
        const groupSums = {};

        // Iterate through groupedData
        data.forEach(item => {
            if (item !== null) {  // Skip null values
                const { value, group } = item;

                // If the group already exists in groupSums, add the value
                if (groupSums[group]) {
                    groupSums[group] += value;
                } else {
                    // If the group doesn't exist, create it and set the initial value
                    groupSums[group] = value;
                }
            }
        });


        this.topCells[0].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Human"], 1)
        this.topCells[1].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Physical"], 1)
        this.topCells[2].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Innovation"], 1)
        this.topCells[3].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Institution"], 1)
        this.topCells[4].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Natural"], 1)
        this.topCells[5].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Social"], 1)

    }

    adjustXAxisGrid(chartArea) {
        const lineGroupBBox = chartArea.querySelector('svg .line-group').getBBox()
        const bottomFirstCol = document.querySelector('#country-scorecard .axis-lables .first-col')
        const bottomAxisGrid = document.querySelector('#country-scorecard .axis-lables .axis-grid')
        bottomAxisGrid.style.width = `${lineGroupBBox.width}px`
        bottomFirstCol.style.width = `${lineGroupBBox.x}px`

        const topFirstCol = document.querySelector('#country-scorecard .legend-row .first-col')
        const topAxisGrid = document.querySelector('#country-scorecard .legend-row .axis-grid')
        topAxisGrid.style.width = `${lineGroupBBox.width}px`
        topFirstCol.style.width = `${lineGroupBBox.x}px`

        // Adjudt Overlay Lines
        const overlayLines = document.querySelector('#country-scorecard .graph-area .overlay-lines')
        overlayLines.style.width = `${lineGroupBBox.width}px`
        overlayLines.style.left = `${lineGroupBBox.x}px`
    }

    populateXAxisLabels(data) {
        const axisCells = document.querySelectorAll('#country-scorecard .axis-lables .axis-grid .cell');
        const len = axisCells.length;

        for (let i = 1; i < len - 2; i++) {
            const cell = axisCells[i];

            // Clear the existing content in the cell
            cell.innerHTML = '';


            // Create a new span element for the data points
            const points = document.createElement('span');
            points.setAttribute('class', 'data-points');
            points.textContent = data[i] != null ? Utils.roundUpToDecimal(data[i].value, 1) : '';

            // Append the new span to the cell
            cell.append(points);
        }
    }


}


export default ScoreGraph